import { useForm } from 'react-hook-form';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertService } from '../../../../services/alert.service';
import { ChannelsService } from '../../../../services/channels.service';
import Input from '../../../../components/forms/input.component';
import Button from '../../../../components/forms/button.component';
import useModal from '../../../../hooks/use-close-modal.hook';
import LogData from '../../../../components/logs/log-data.component';
import Icon from '../../../../components/forms/icon.component';
import ReCAPTCHA from 'react-google-recaptcha';
import { useDarkMode } from '../../../../hooks/use-dark-mode.hook';

interface SendEmailProps {
  show: boolean;
  onRequestClose: (refresh: boolean) => void;
  payload: any;
}

function SendEmail({ show, onRequestClose, payload }: SendEmailProps) {
  const { t } = useTranslation();
  const modalRef = useRef<HTMLDivElement>(null);
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const isDarkTheme = useDarkMode();

  useModal(modalRef, show);

  const handleCaptchaChange = (token: string | null) => {
    setRecaptchaToken(token);
  };

  const defaultValues = {
    email: '',
    email_account: ''
  };

  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isValid, isSubmitting }
  } = useForm({
    mode: 'onChange',
    defaultValues
  });

  const sendEmail = async (data: any, buttonId: string) => {
    if (!recaptchaToken) {
      AlertService.toastInfo('¡ Atención !, debes completar el reCAPTCHA.');
      return;
    }

    try {
      const confirmed = await AlertService.question(
        t('general.atention'),
        `¿Estás seguro de enviar el email de prueba a ${data.email}?`,
        null as any
      );
      if (!confirmed) {
        return;
      }

      const submitData = {
        output: data.email_account,
        email: data.email,
        recaptcha: recaptchaToken
      };

      await ChannelsService.sendMessageTestEmail(submitData);
      await onRequestClose(true);
      AlertService.toastSuccess('Email enviado correctamente');
      reset(defaultValues);

      recaptchaRef.current?.reset();
      setRecaptchaToken(null);
      LogData(submitData, buttonId, window.location.href);
    } catch (errorCode: any) {
      console.log('Error:', errorCode);
      AlertService.toastError(errorCode.error || errorCode);
      recaptchaRef.current?.reset();
      setRecaptchaToken(null);
    }
  };

  const resetForm = () => {
    onRequestClose(false);
    reset(defaultValues);

    recaptchaRef.current?.reset();
    setRecaptchaToken(null);
  };

  useEffect(() => {
    if (payload?._id) {
      reset({
        email: '',
        email_account: payload._id
      });

      recaptchaRef.current?.reset();
      setRecaptchaToken(null);
    }
  }, [payload, reset]);

  return (
    <div className="modal fade" ref={modalRef} data-bs-backdrop="static">
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header justify-content-end border-0 pb-0">
            <div
              className="btn btn-sm btn-icon btn-active-light-primary"
              onClick={() => resetForm()}
            >
              <Icon icon="ki-cross" size="fs-2x" />
            </div>
          </div>
          <form onSubmit={handleSubmit((data) => sendEmail(data, 'sendEmailTest-34463874687'))}>
            <div className="modal-body pt-0 pb-15 px-5 px-xl-20">
              <div className="mt-1 text-center">
                <h1>Enviar Email de Prueba</h1>
                <h5 className="text-primary fw-bolder">{payload?.name}</h5>
                <div className="text-muted fw-semibold fs-5">
                  <span>Envía un email de prueba para verificar la configuración de tu cuenta</span>
                </div>
                <div className="separator border-2 my-5"></div>
              </div>

              <div className="row my-10 px-10">
                <div className="col-lg-12 mb-10">
                  <div className="fv-row mb-0">
                    <Input
                      label="Dirección de Email"
                      requiredIndicator="required"
                      placeholder="ejemplo@correo.com"
                      type="email"
                      errors={errors.email}
                      {...register('email', {
                        required: {
                          value: true,
                          message: 'El email es requerido'
                        },
                        setValueAs: (value) => value.trim(),
                        pattern: {
                          value: emailRegex,
                          message: 'Ingresa un email válido'
                        }
                      })}
                    />
                  </div>
                </div>
                <div className="text-center mb-10">
                  <h5 className="mb-3 text-danger fw-bolder fs-1">¡ Atención !</h5>
                  <div className="fs-5 fw-bolder text-start">
                    Si no ves el correo en tu bandeja de entrada, verifica que no este en tu carpeta
                    de SPAM o Correos no deseados. Ahora para enviar el correo de prueba, asegúrate
                    de validar correctamente el reCAPTCHA.
                  </div>
                </div>
                <img
                  src="/assets/media/illustrations/undraw/verify.svg"
                  alt="Verify"
                  className="ms-10"
                  width={'20px'}
                />
                <div className="d-flex justify-content-center mb-4">
                  <ReCAPTCHA
                    sitekey="6Lfec20rAAAAANyGkUlot_jUEWAbexPdSk8Nw_lu"
                    onChange={handleCaptchaChange}
                    theme={isDarkTheme ? 'dark' : 'light'}
                    ref={recaptchaRef}
                  />
                </div>
              </div>

              <div className="d-flex flex-center flex-row-fluid pt-0">
                <button
                  type="button"
                  className="btn btn-light hover-scale"
                  onClick={() => resetForm()}
                >
                  {t('general.close')}
                </button>
                <Button
                  type="submit"
                  className="btn btn-primary ms-3 hover-scale"
                  disabled={!isValid || !recaptchaToken}
                  isLoading={isSubmitting}
                >
                  Enviar Email
                  <Icon icon="ki-send" size="fs-1" className="ms-2" />
                </Button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default SendEmail;
